import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Paper, Typography, Alert, LinearProgress, IconButton, Tooltip } from '@mui/material';
import { CheckCircle as CompleteIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { devLog } from '../../../utils/devLog';
import { extractRiseCourseProgress } from './riseSuspendData';

interface ScormPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

interface ScormRestore {
    status?: string;
    score?: string | null;
    suspendData?: { data?: string; location?: string } | null;
}

// Restore-Zustand als URL-Fragment an die Wrapper-URL hängen. Der Wrapper
// (ScormController) wird unauthentifiziert als iframe-src geladen — ein
// Fragment ist der einzige Kanal, der ohne Bearer-Token/neue Auth-Fläche
// auskommt und serverseitig nicht geloggt wird.
const buildScormFragment = (restore: ScormRestore | null): string => {
    if (!restore) return '';
    const suspend = restore.suspendData?.data ?? '';
    const location = restore.suspendData?.location ?? '';
    const started = !!restore.status && restore.status !== 'not_started';
    if (!suspend && !location && !started) return '';

    const status = restore.status;
    const payload = {
        entry: 'resume',
        suspend_data: suspend,
        location,
        lesson_status: status === 'passed' ? 'passed' : status === 'failed' ? 'failed' : status === 'completed' ? 'completed' : 'incomplete',
        completion_status: status === 'completed' || status === 'passed' ? 'completed' : 'incomplete',
        success_status: status === 'passed' ? 'passed' : status === 'failed' ? 'failed' : 'unknown',
        score_raw: restore.score ?? '',
    };
    return '#scorm=' + encodeURIComponent(JSON.stringify(payload));
};

const ScormPlayer: React.FC<ScormPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const [scormLoaded, setScormLoaded] = useState(false);
    const [scormCompleted, setScormCompleted] = useState(false);
    const [scormScore, setScormScore] = useState<number | null>(null);
    const [error, setError] = useState<string>('');
    const [extractedLaunchUrl, setExtractedLaunchUrl] = useState<string>('');
    const [extracting, setExtracting] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [restore, setRestore] = useState<ScormRestore | null>(null);
    const [restoreResolved, setRestoreResolved] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const autoCompletedRef = useRef(false);
    // Laufender Save-Zustand für suspend_data/location: kommen als getrennte
    // SetValue-Calls, werden aber zu einem Blob zusammengeführt.
    const suspendRef = useRef<{ data?: string; location?: string }>({});
    const lastRiseProgressRef = useRef<number | null>(null);

    // Handle fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!isFullscreen) {
            const el = containerRef.current;
            if (el?.requestFullscreen) {
                el.requestFullscreen();
            } else if ((el as any)?.webkitRequestFullscreen) {
                (el as any).webkitRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            }
        }
    }, [isFullscreen]);

    // Sync state with actual fullscreen changes (including Esc key)
    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        document.addEventListener('webkitfullscreenchange', handler);
        return () => {
            document.removeEventListener('fullscreenchange', handler);
            document.removeEventListener('webkitfullscreenchange', handler);
        };
    }, []);

    const content = lesson.content || {};
    const { packageUrl, launchUrl, completionThreshold = 100, masteryScore = 80 } = content;

    useEffect(() => {
        autoCompletedRef.current = false;
        setScormCompleted(false);
        setScormScore(null);
        suspendRef.current = {};
        lastRiseProgressRef.current = null;
    }, [lesson.id]);

    useEffect(() => {
        if (autoCompletedRef.current || !scormCompleted) return;
        if (scormScore !== null && scormScore < masteryScore) return;
        autoCompletedRef.current = true;
        onComplete();
    }, [scormCompleted, scormScore, masteryScore, onComplete]);

    // Persist den zusammengeführten suspend_data/location-Blob.
    const saveSuspend = useCallback(() => {
        onProgressUpdate?.(lesson.id, { suspendData: { ...suspendRef.current } });
    }, [lesson.id, onProgressUpdate]);

    const handleScormInit = useCallback(() => {
        setScormLoaded(true);
        // Jeder Reload ruft erneut Initialize — ein bereits gespeicherter
        // Abschluss darf nicht auf in_progress zurückfallen. Kein
        // progressPercentage aus dem Init (kennt es nicht); echte Werte kommen
        // aus progress_measure/lesson_status.
        const alreadyDone = restore?.status === 'completed' || restore?.status === 'passed';
        if (!alreadyDone) {
            onProgressUpdate?.(lesson.id, { status: 'in_progress' });
        }
    }, [lesson.id, onProgressUpdate, restore]);

    // Einziger SCORM-Datenmodell-Handler; von window.API (Same-Origin/Tests)
    // UND dem postMessage-Listener (echter Cross-Origin-Pfad) genutzt.
    const applyScormElement = useCallback((element: string, value: string) => {
        switch (element) {
            // --- SCORM 1.2 ---
            case 'cmi.core.lesson_status':
                if (value === 'completed' || value === 'passed') {
                    setScormCompleted(true);
                    onProgressUpdate?.(lesson.id, {
                        status: value === 'passed' ? 'passed' : 'completed',
                        progressPercentage: 100,
                    });
                }
                break;
            case 'cmi.core.score.raw': {
                const score = parseFloat(value);
                if (!isNaN(score)) {
                    setScormScore(score);
                    onProgressUpdate?.(lesson.id, { score: value });
                }
                break;
            }
            case 'cmi.core.lesson_location':
                suspendRef.current.location = value;
                saveSuspend();
                break;

            // --- SCORM 2004 ---
            case 'cmi.completion_status':
                if (value === 'completed') {
                    setScormCompleted(true);
                    onProgressUpdate?.(lesson.id, { status: 'completed', progressPercentage: 100 });
                }
                break;
            case 'cmi.success_status':
                if (value === 'passed') {
                    setScormCompleted(true);
                    onProgressUpdate?.(lesson.id, { status: 'passed', progressPercentage: 100 });
                } else if (value === 'failed') {
                    onProgressUpdate?.(lesson.id, { status: 'failed' });
                }
                break;
            // Teilfortschritt gibt es nur in SCORM 2004; 1.2 kennt kein
            // Zwischen-% und springt 0 → 100 beim Abschluss.
            case 'cmi.progress_measure': {
                const measure = parseFloat(value);
                if (!isNaN(measure)) {
                    const pct = Math.round(Math.min(1, Math.max(0, measure)) * 100);
                    onProgressUpdate?.(lesson.id, { status: 'in_progress', progressPercentage: pct });
                }
                break;
            }
            case 'cmi.score.scaled':
            case 'cmi.score.raw': {
                const score = parseFloat(value);
                if (!isNaN(score)) {
                    const scorePercent = element === 'cmi.score.scaled' ? score * 100 : score;
                    setScormScore(scorePercent);
                    onProgressUpdate?.(lesson.id, { score: value });
                }
                break;
            }
            case 'cmi.location':
                suspendRef.current.location = value;
                saveSuspend();
                break;

            // --- beide Versionen ---
            case 'cmi.suspend_data': {
                suspendRef.current.data = value;
                saveSuspend();
                const risePct = extractRiseCourseProgress(value);
                if (
                    risePct !== null
                    && risePct > 0
                    && risePct !== lastRiseProgressRef.current
                    && !scormCompleted
                    && restore?.status !== 'completed'
                    && restore?.status !== 'passed'
                ) {
                    lastRiseProgressRef.current = risePct;
                    onProgressUpdate?.(lesson.id, { status: 'in_progress', progressPercentage: risePct });
                }
                break;
            }
        }
    }, [lesson.id, onProgressUpdate, saveSuspend, scormCompleted, restore]);

    // window.API + postMessage-Brücke zum Wrapper (cross-origin, s.o.)
    useEffect(() => {
        const apiOrigin = (() => {
            try {
                return new URL(process.env.REACT_APP_API_URL || '').origin;
            } catch {
                return '';
            }
        })();

        const scormApi = {
            LMSInitialize: () => { handleScormInit(); return 'true'; },
            LMSFinish: () => 'true',
            LMSGetValue: (element: string) => {
                const defaults: Record<string, string> = {
                    'cmi.core.lesson_status': 'incomplete',
                    'cmi.core.score.raw': '',
                    'cmi.core.score.min': '0',
                    'cmi.core.score.max': '100',
                    'cmi.core.student_id': 'student_001',
                    'cmi.core.student_name': 'Student',
                };
                return defaults[element] || '';
            },
            LMSSetValue: (element: string, value: string) => { applyScormElement(element, value); return 'true'; },
            LMSCommit: () => 'true',
            LMSGetLastError: () => '0',
            LMSGetErrorString: () => 'No error',
            LMSGetDiagnostic: () => '',
        };

        const scorm2004Api = {
            Initialize: () => { handleScormInit(); return 'true'; },
            Terminate: () => 'true',
            GetValue: (element: string) => {
                const defaults: Record<string, string> = {
                    'cmi.completion_status': 'incomplete',
                    'cmi.success_status': 'unknown',
                    'cmi.learner_id': 'student_001',
                    'cmi.learner_name': 'Student',
                };
                return defaults[element] || '';
            },
            SetValue: (element: string, value: string) => { applyScormElement(element, value); return 'true'; },
            Commit: () => 'true',
            GetLastError: () => '0',
            GetErrorString: () => 'No error',
            GetDiagnostic: () => '',
        };

        (window as any).API = scormApi;
        (window as any).API_1484_11 = scorm2004Api;
        try {
            if (window.top && window.top !== window) {
                (window.top as any).API = scormApi;
                (window.top as any).API_1484_11 = scorm2004Api;
            }
        } catch (e) {
            devLog('Could not set API on window.top (cross-origin)');
        }

        // Der Wrapper (API-Origin) relayt jeden SCORM-Call hierher, weil der
        // Content cross-origin window.API des Frontends nicht erreicht.
        const onMessage = (event: MessageEvent) => {
            const data = event.data;
            if (!data || data.type !== 'scorm') return;
            // Fail-closed gegen gefälschte Abschlüsse fremder Frames.
            if (!apiOrigin || event.origin !== apiOrigin) return;
            if (event.source !== iframeRef.current?.contentWindow) return;
            if (data.action === 'LMSInitialize' || data.action === 'Initialize') {
                handleScormInit();
            } else if (data.action === 'LMSSetValue' || data.action === 'SetValue') {
                applyScormElement(data.element, data.value);
            }
        };
        window.addEventListener('message', onMessage);

        return () => {
            window.removeEventListener('message', onMessage);
            delete (window as any).API;
            delete (window as any).API_1484_11;
            try {
                if (window.top && window.top !== window) {
                    delete (window.top as any).API;
                    delete (window.top as any).API_1484_11;
                }
            } catch (e) {
                // Ignore cross-origin errors
            }
        };
    }, [handleScormInit, applyScormElement]);

    // Gespeicherten Fortschritt für Resume laden (userId kommt serverseitig
    // aus dem JWT). Ohne Token übersprungen → frischer Start.
    useEffect(() => {
        let cancelled = false;
        setRestore(null);
        setRestoreResolved(false);

        const token = localStorage.getItem('token');
        if (!token || typeof fetch !== 'function') {
            setRestoreResolved(true);
            return;
        }

        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/progress/lesson/${lesson.id}`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                });
                if (!cancelled && res.ok) {
                    const data = await res.json();
                    setRestore({ status: data.status, score: data.score, suspendData: data.suspendData });
                }
            } catch (err) {
                devLog('Resume fetch failed', err);
            } finally {
                if (!cancelled) setRestoreResolved(true);
            }
        })();

        return () => { cancelled = true; };
    }, [lesson.id]);

    // Extract and load SCORM package
    useEffect(() => {
        devLog('SCORM Player - packageUrl:', packageUrl);
        devLog('SCORM Player - launchUrl:', launchUrl);

        if (!packageUrl) {
            setError('Kein SCORM-Paket konfiguriert');
            return;
        }

        // For media library files, extract the ZIP and get launch URL
        if (packageUrl.startsWith('/api/media/')) {
            devLog('Loading via media library path');
            const extractAndLoad = async () => {
                setExtracting(true);
                try {
                    const token = localStorage.getItem('token');
                    const uuid = packageUrl.split('/')[3];

                    // Step 1: Extract the package
                    const extractResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/scorm/extract/${uuid}`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!extractResponse.ok) {
                        throw new Error('Failed to extract SCORM package');
                    }

                    await extractResponse.json();

                    // Step 2: Get launch URL
                    const launchResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/scorm/launch/${uuid}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!launchResponse.ok) {
                        throw new Error('Failed to get launch URL');
                    }

                    const launchData = await launchResponse.json();
                    devLog('Launch data received:', launchData);

                    // Use the launchUrl from the response
                    if (launchData.launchUrl) {
                        const fullUrl = `${process.env.REACT_APP_API_URL}${launchData.launchUrl}`;
                        devLog('Setting launch URL to:', fullUrl);
                        setExtractedLaunchUrl(fullUrl);
                    } else {
                        console.error('No launchUrl in response:', launchData);
                        setError('Launch URL nicht gefunden');
                    }
                } catch (err) {
                    console.error('Error loading SCORM package:', err);
                    setError('Fehler beim Laden des SCORM-Pakets: ' + (err as Error).message);
                } finally {
                    setExtracting(false);
                }
            };
            extractAndLoad();
        } else {
            // External URL or old-style launchUrl
            const url = launchUrl || packageUrl;
            devLog('Using external/fallback URL:', url);

            // Check if it's an old /api/scorm/player/{uuid} URL
            if (url && url.includes('/api/scorm/player/')) {
                devLog('Detected old /api/scorm/player/ URL, converting...');
                // Extract UUID and load via new API
                const uuidMatch = url.match(/\/api\/scorm\/player\/([^\/\?]+)/);
                if (uuidMatch) {
                    const uuid = uuidMatch[1];
                    const loadViaNewApi = async () => {
                        setExtracting(true);
                        try {
                            const token = localStorage.getItem('token');
                            const launchResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/scorm/launch/${uuid}`, {
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            });

                            if (launchResponse.ok) {
                                const launchData = await launchResponse.json();
                                if (launchData.launchUrl) {
                                    setExtractedLaunchUrl(`${process.env.REACT_APP_API_URL}${launchData.launchUrl}`);
                                } else {
                                    setExtractedLaunchUrl(url);
                                }
                            } else {
                                setExtractedLaunchUrl(url);
                            }
                        } catch (err) {
                            console.error('Error loading via new API, using old URL:', err);
                            setExtractedLaunchUrl(url);
                        } finally {
                            setExtracting(false);
                        }
                    };
                    loadViaNewApi();
                } else {
                    setExtractedLaunchUrl(url);
                }
            } else {
                // Use URL directly
                setExtractedLaunchUrl(url);
            }
        }
    }, [packageUrl, launchUrl]);

    const handleManualComplete = () => {
        onComplete();
    };

    const meetsCompletionRequirements = () => {
        if (!scormCompleted) return false;
        if (scormScore !== null && scormScore < masteryScore) return false;
        return true;
    };

    const iframeSrc = extractedLaunchUrl + buildScormFragment(restore);

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {(extracting || (!error && extractedLaunchUrl && !restoreResolved)) && (
                    <Box sx={{ mb: 2 }}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                            SCORM-Paket wird geladen...
                        </Typography>
                    </Box>
                )}

                {!error && !extracting && extractedLaunchUrl && restoreResolved && (
                    <Box>
                        {/* SCORM Status */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Status: {scormCompleted ? 'Abgeschlossen' : 'In Bearbeitung'}
                            </Typography>
                            {scormScore !== null && (
                                <Typography variant="body2" color="text.secondary">
                                    Punktzahl: {scormScore}% (Mindestens {masteryScore}% erforderlich)
                                </Typography>
                            )}
                        </Box>

                        {/* SCORM Content iframe */}
                        <Box
                            ref={containerRef}
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: isFullscreen ? '100vh' : '600px',
                                border: isFullscreen ? 'none' : '1px solid',
                                borderColor: 'divider',
                                borderRadius: isFullscreen ? 0 : 1,
                                overflow: 'hidden',
                                bgcolor: isFullscreen ? '#000' : 'transparent',
                            }}
                        >
                            <iframe
                                key={lesson.id}
                                ref={iframeRef}
                                src={iframeSrc}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }}
                                title="SCORM Content"
                                allowFullScreen
                            />
                            <Tooltip title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
                                <IconButton
                                    onClick={toggleFullscreen}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                        zIndex: 10,
                                    }}
                                    size="small"
                                >
                                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Completion Alert */}
                        {scormCompleted && (
                            <Alert
                                severity={meetsCompletionRequirements() ? 'success' : 'warning'}
                                sx={{ mt: 2 }}
                            >
                                {meetsCompletionRequirements() ? (
                                    <Typography>
                                        SCORM-Paket erfolgreich abgeschlossen!
                                        {scormScore !== null && ` Punktzahl: ${scormScore}%`}
                                    </Typography>
                                ) : (
                                    <Typography>
                                        SCORM-Paket abgeschlossen, aber die Mindestpunktzahl wurde nicht erreicht.
                                        Erforderlich: {masteryScore}%, Erreicht: {scormScore}%
                                    </Typography>
                                )}
                            </Alert>
                        )}
                    </Box>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<CompleteIcon />}
                    onClick={handleManualComplete}
                    disabled={!meetsCompletionRequirements() && scormCompleted}
                >
                    {scormCompleted && meetsCompletionRequirements()
                        ? 'Bestätigen'
                        : 'Als abgeschlossen markieren'}
                </Button>
            </Box>
        </Box>
    );
};

export default ScormPlayer;
